/*  OrderStatsWidget – shows order counts for six time-windows
    ---------------------------------------------------------------- */
import React, {useMemo} from 'react';
import moment from 'moment';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';

import useDepotStats from '../useDepotStats';
import OrderStatsCard from './OrderStatsCard';

import './OrderStatsWidget.scss';

export default function OrdersStatsWidget() {
  /* -------- derive six date windows (memoised) --------
     Always relative to “now”, so the widget ignores any external date filter. */
  const windows = useMemo(() => {
    const mk = (
      d: moment.MomentInput,
      s: 'startOf' | 'endOf',
      u: moment.unitOfTime.StartOf,
    ) => moment(d)[s](u).toDate();

    const now = moment(); // current moment

    return {
      today: [mk(now, 'startOf', 'day'), mk(now, 'endOf', 'day')] as const,
      yesterday: [
        mk(now.clone().subtract(1, 'day'), 'startOf', 'day'),
        mk(now.clone().subtract(1, 'day'), 'endOf', 'day'),
      ] as const,
      thisWeek: [mk(now, 'startOf', 'week'), mk(now, 'endOf', 'week')] as const,
      lastWeek: [
        mk(now.clone().subtract(1, 'week'), 'startOf', 'week'),
        mk(now.clone().subtract(1, 'week'), 'endOf', 'week'),
      ] as const,
      thisMonth: [
        mk(now, 'startOf', 'month'),
        mk(now, 'endOf', 'month'),
      ] as const,
      lastMonth: [
        mk(now.clone().subtract(1, 'month'), 'startOf', 'month'),
        mk(now.clone().subtract(1, 'month'), 'endOf', 'month'),
      ] as const,
    };
  }, []); // ← empty deps → doesn’t recalc on external state changes

  /* -------- fetch stats for every window (cache-aware) -------- */
  const {loading: ldToday, stats: todayStat} = useDepotStats(...windows.today);
  const {loading: ldYday, stats: ydayStat} = useDepotStats(
    ...windows.yesterday,
  );
  const {loading: ldThisWk, stats: thisWeekStat} = useDepotStats(
    ...windows.thisWeek,
  );
  const {loading: ldLastWk, stats: lastWeekStat} = useDepotStats(
    ...windows.lastWeek,
  );
  const {loading: ldThisMon, stats: thisMonthStat} = useDepotStats(
    ...windows.thisMonth,
  );
  const {loading: ldLastMon, stats: lastMonthStat} = useDepotStats(
    ...windows.lastMonth,
  );

  // Use default values if stats are undefined to prevent infinite skeleton loading
  const safeTodayStat = todayStat || {invoicesCount: 0};
  const safeYdayStat = ydayStat || {invoicesCount: 0};
  const safeThisWeekStat = thisWeekStat || {invoicesCount: 0};
  const safeLastWeekStat = lastWeekStat || {invoicesCount: 0};
  const safeThisMonthStat = thisMonthStat || {invoicesCount: 0};
  const safeLastMonthStat = lastMonthStat || {invoicesCount: 0};

  // Only show skeleton if all are loading AND no stats available
  const anyLoading =
    ldToday || ldYday || ldThisWk || ldLastWk || ldThisMon || ldLastMon;
  const hasNoStats =
    !todayStat &&
    !ydayStat &&
    !thisWeekStat &&
    !lastWeekStat &&
    !thisMonthStat &&
    !lastMonthStat;

  if (anyLoading && hasNoStats) {
    return <Skeleton variant="rect" height={150} style={{borderRadius: 8}} />;
  }

  /* -------- render -------- */
  return (
    <Box className="order-stats-widget">
      {/* heading added */}
      <Typography className="widget-heading">ORDER STATS</Typography>

      <OrderStatsCard
        link="/orders"
        today={safeTodayStat.invoicesCount ?? 0}
        yesterday={safeYdayStat.invoicesCount ?? 0}
        thisWeek={safeThisWeekStat.invoicesCount ?? 0}
        lastWeek={safeLastWeekStat.invoicesCount ?? 0}
        thisMonth={safeThisMonthStat.invoicesCount ?? 0}
        lastMonth={safeLastMonthStat.invoicesCount ?? 0}
      />
    </Box>
  );
}
